import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { endpoints } from '@/modules/jobs/constants';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';
import type { JobAutomationUpdateRequestApi } from '@/modules/jobs/types';

interface UpdateJobAutomationParams {
  jobId: string;
  body: JobAutomationUpdateRequestApi;
}

/**
 * `PATCH /jobs/{id}/automation`: omitted fields stay unchanged. The response
 * is the updated job, written into the detail cache before the list refetch.
 */
export const useUpdateJobAutomationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, body }: UpdateJobAutomationParams) => {
      const { data } = await fetchClient.PATCH(endpoints.jobAutomation, {
        params: { path: { id: jobId } },
        body,
      });
      return data;
    },
    onSuccess: (job, { jobId }) => {
      if (job) queryClient.setQueryData(jobsQueryKeys.detail(jobId), job);
    },
    onSettled: (_data, _error, { jobId }) => {
      void queryClient.invalidateQueries({
        queryKey: jobsQueryKeys.detail(jobId),
      });
      void queryClient.invalidateQueries({ queryKey: jobsQueryKeys.lists() });
    },
  });
};
